/**
 * Two-step Pipeline Report import dialog (Reporting → Pipeline Report tab).
 *
 * Step 1 ("pick"): pick a .csv/.xlsx file, POST it with commit=false to get a dry-run
 * preview — nothing is written yet.
 * Step 2 ("review"): show the preview's per-row diffs, grouped so rows that will
 * actually change are easy to scan, separate from no-op matches and unmatched rows.
 * "Confirm Import" re-POSTs the SAME File with commit=true.
 * Step 3 ("done"): a brief success summary, then the table refetches on close.
 *
 * Update-only — see src/lib/pipeline-report-import.ts. No row is ever created; a
 * "matched" row with no `changes` simply means the file agreed with the current data.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/components/ui/status-badge";
import { useConstraintLibrary } from "@/hooks/use-accounts";
import { useImportPipelineReport } from "@/hooks/use-pipeline-report";
import { useUsers } from "@/hooks/use-users";
import { formatDateMDY } from "@/lib/format";
import { jsonFetch } from "@/lib/json-fetch";
import { formatPlanMoney } from "@/lib/plan-format";
import { pipelineReportKeys } from "@/lib/query";
import type {
  PipelineReportImportResult,
  PipelineReportImportRowResult,
} from "@/schemas/pipeline-report";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type Step = "pick" | "review" | "done";

// Ad hoc query — mirrors the identically-named private hook in PipelineReportTable.tsx
// (same query key), so this dialog's id→name resolution shares that cache rather than
// firing a second request when the table is already mounted.
function useResourceOptions() {
  return useQuery({
    queryKey: ["dashboard-resources"],
    queryFn: () =>
      jsonFetch<{ resources: { id: string; name: string }[] }>("/api/dashboard/resources"),
  });
}

const FIELD_LABELS: Record<string, string> = {
  stage: "Stage",
  resourceId: "Resource",
  resourcingStatus: "Resourcing Status",
  resourcePlanReceived: "Resource Plan Received",
  notes: "Notes",
  ownerUserId: "Owner",
  platformConstraintId: "Platform",
  total: "Total",
  leadSourceId: "Lead Source",
  commitDate: "Commit",
};

const RESOURCING_STATUS_LABEL: Record<string, string> = {
  Staffed: "Staffed",
  PartiallyStaffed: "Partially Staffed",
  NotStaffed: "Not Staffed",
};

const LOOKUP_FIELDS = new Set([
  "resourceId",
  "ownerUserId",
  "platformConstraintId",
  "leadSourceId",
]);

/** Renders one side of a change's from/to pair. `nameById` resolves the lookup fields'
 * raw ids (resourceId/ownerUserId/platformConstraintId/leadSourceId) — the backend diff
 * only carries the resolved id, not the display name. */
function formatChangeValue(field: string, value: unknown, nameById: Map<string, string>): string {
  if (value === null || value === undefined || value === "") return "—";
  if (field === "resourcingStatus" && typeof value === "string") {
    return RESOURCING_STATUS_LABEL[value] ?? value;
  }
  if (field === "resourcePlanReceived") {
    return value === true ? "Yes" : value === false ? "No" : String(value);
  }
  if (field === "total" && typeof value === "string") return formatPlanMoney(value);
  if (field === "commitDate" && typeof value === "string") return formatDateMDY(value);
  if (LOOKUP_FIELDS.has(field) && typeof value === "string") {
    return nameById.get(value) ?? value;
  }
  return typeof value === "string" ? value : String(value);
}

function ImportRowItem({
  row,
  nameById,
}: {
  row: PipelineReportImportRowResult;
  nameById: Map<string, string>;
}) {
  if (row.status === "unmatched") {
    return (
      <li className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm">
        <div className="flex items-center justify-between gap-2">
          <p className="font-medium">
            {row.account || "—"} · {row.deal || "—"}
          </p>
          <StatusBadge status="failed" className="h-5 shrink-0 px-1.5">
            Unmatched
          </StatusBadge>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          Row {row.rowNumber} — {row.reason}
        </p>
      </li>
    );
  }

  const changes = row.changes ?? [];

  return (
    <li className="rounded-md border border-border px-3 py-2 text-sm">
      <div className="flex items-center justify-between gap-2">
        <p className="font-medium">
          {row.account} · {row.deal}
        </p>
        <StatusBadge status="completed" className="h-5 shrink-0 px-1.5">
          Matched
        </StatusBadge>
      </div>
      {changes.length > 0 ? (
        <ul className="mt-1.5 space-y-1">
          {changes.map((c) => (
            <li key={c.field} className="flex flex-wrap items-baseline gap-1.5 text-xs">
              <span className="font-medium text-muted-foreground">
                {FIELD_LABELS[c.field] ?? c.field}:
              </span>
              <span className="text-muted-foreground line-through">
                {formatChangeValue(c.field, c.from, nameById)}
              </span>
              <span className="text-muted-foreground">→</span>
              <span className="font-medium text-foreground">
                {formatChangeValue(c.field, c.to, nameById)}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-1 text-xs text-muted-foreground">No changes</p>
      )}
      {row.warnings && row.warnings.length > 0 && (
        <ul className="mt-1.5 space-y-1 rounded border border-warning-200 bg-warning-50/60 px-2 py-1.5 dark:border-warning-900/40 dark:bg-warning-950/20">
          {row.warnings.map((w) => (
            <li
              key={w}
              className="flex items-start gap-1.5 text-xs text-warning-700 dark:text-warning-300"
            >
              <AlertTriangle className="mt-0.5 size-3 shrink-0" />
              <span>{w}</span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function ImportRowGroup({
  title,
  rows,
  nameById,
}: {
  title: string;
  rows: PipelineReportImportRowResult[];
  nameById: Map<string, string>;
}) {
  if (rows.length === 0) return null;
  return (
    <section>
      <h3 className="mb-1.5 px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title} ({rows.length})
      </h3>
      <ul className="space-y-1.5">
        {rows.map((row) => (
          <ImportRowItem key={row.rowNumber} row={row} nameById={nameById} />
        ))}
      </ul>
    </section>
  );
}

export function PipelineReportImportDialog({ open, onOpenChange }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [step, setStep] = useState<Step>("pick");
  const [preview, setPreview] = useState<PipelineReportImportResult | null>(null);
  const [result, setResult] = useState<PipelineReportImportResult | null>(null);

  const importPipelineReport = useImportPipelineReport();
  const qc = useQueryClient();

  // Id→name resolution for the lookup-field diffs (Resource/Owner/Platform) — the
  // backend diff carries only the resolved id (see
  // src/lib/pipeline-report-import.ts's applyLookupField). Lead Source dropped out
  // of this map when it became a gateway-sourced read-only field (LOOKUP_FIELDS/
  // FIELD_LABELS below still carry "leadSourceId" for backward-compatible display
  // of any pre-existing diff payload; an unresolved id just falls back to itself).
  const resourcesQuery = useResourceOptions();
  const usersQuery = useUsers({});
  const constraintLibraryQuery = useConstraintLibrary();

  const nameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of resourcesQuery.data?.resources ?? []) m.set(r.id, r.name);
    for (const u of usersQuery.data ?? []) m.set(u.id, u.name);
    for (const c of constraintLibraryQuery.data ?? []) m.set(c.id, c.name);
    return m;
  }, [resourcesQuery.data, usersQuery.data, constraintLibraryQuery.data]);

  const grouped = useMemo(() => {
    const rows = preview?.rows ?? [];
    return {
      withChanges: rows.filter((r) => r.status === "matched" && (r.changes?.length ?? 0) > 0),
      noChanges: rows.filter((r) => r.status === "matched" && (r.changes?.length ?? 0) === 0),
      unmatched: rows.filter((r) => r.status === "unmatched"),
    };
  }, [preview]);

  function reset() {
    setFile(null);
    setStep("pick");
    setPreview(null);
    setResult(null);
  }

  function handleOpenChange(next: boolean) {
    if (!next) reset();
    onOpenChange(next);
  }

  function handlePreviewSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!file) return;
    importPipelineReport.mutate(
      { file, commit: false },
      {
        onSuccess: (res) => {
          setPreview(res);
          setStep("review");
        },
      },
    );
  }

  function handleBack() {
    setPreview(null);
    setStep("pick");
  }

  function handleConfirm() {
    if (!file) return;
    importPipelineReport.mutate(
      { file, commit: true },
      {
        onSuccess: (res) => {
          setResult(res);
          setStep("done");
          qc.invalidateQueries({ queryKey: pipelineReportKeys.all });
          toast.success(
            res.matchedCount > 0
              ? `${res.matchedCount} row(s) updated.`
              : "Import complete — no rows needed updating.",
          );
        },
      },
    );
  }

  const pending = importPipelineReport.isPending;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl">
        {step === "pick" && (
          <>
            <DialogHeader>
              <DialogTitle>Import Pipeline Report</DialogTitle>
              <DialogDescription>
                Upload a Pipeline Report export (.csv or .xlsx). Rows are matched to existing
                Pipeline Report entries by Account + Deal and only update those rows — this never
                creates a new entry.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handlePreviewSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="pipeline-import-file">Pipeline Report File</Label>
                <input
                  id="pipeline-import-file"
                  type="file"
                  accept=".csv,.xlsx"
                  onChange={(e) => {
                    const picked = e.target.files?.[0];
                    setFile(picked ?? null);
                  }}
                  className="block w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground file:mr-3 file:rounded file:border-0 file:bg-muted file:px-2 file:py-0.5 file:text-sm file:font-medium"
                />
                <p className="text-xs text-muted-foreground">Accepted: .csv, .xlsx</p>
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                  disabled={pending}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={!file || pending}>
                  {pending ? "Previewing…" : "Preview Import"}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}

        {step === "review" && preview && (
          <>
            <DialogHeader>
              <DialogTitle>Review Import</DialogTitle>
              <DialogDescription>
                {preview.matchedCount} of {preview.totalDataRows} row(s) matched
                {preview.unmatchedCount > 0 ? ` · ${preview.unmatchedCount} unmatched` : ""}.
                Nothing has been saved yet.
              </DialogDescription>
            </DialogHeader>

            {preview.ignoredColumns.length > 0 && (
              <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                The following columns were in the file but can't be imported (they're computed
                automatically): {preview.ignoredColumns.join(", ")}.
              </p>
            )}

            <div className="max-h-[420px] space-y-4 overflow-y-auto rounded-md border border-border p-2">
              {preview.rows.length === 0 ? (
                <p className="px-1 py-2 text-sm text-muted-foreground">
                  No data rows in this file.
                </p>
              ) : (
                <>
                  <ImportRowGroup
                    title="Will update"
                    rows={grouped.withChanges}
                    nameById={nameById}
                  />
                  <ImportRowGroup title="No changes" rows={grouped.noChanges} nameById={nameById} />
                  <ImportRowGroup title="Unmatched" rows={grouped.unmatched} nameById={nameById} />
                </>
              )}
            </div>

            <DialogFooter className="sm:justify-between">
              <Button type="button" variant="outline" onClick={handleBack} disabled={pending}>
                Back
              </Button>
              <div className="flex items-center gap-3">
                {preview.matchedCount === 0 && (
                  <span className="text-xs text-muted-foreground">Nothing to import</span>
                )}
                <Button
                  type="button"
                  onClick={handleConfirm}
                  disabled={pending || preview.matchedCount === 0}
                >
                  {pending ? "Importing…" : "Confirm Import"}
                </Button>
              </div>
            </DialogFooter>
          </>
        )}

        {step === "done" && result && (
          <>
            <DialogHeader>
              <DialogTitle>Import Complete</DialogTitle>
              <DialogDescription>
                {result.matchedCount > 0
                  ? `${result.matchedCount} row(s) updated.`
                  : "No rows needed updating."}
                {result.unmatchedCount > 0 ? ` ${result.unmatchedCount} row(s) unmatched.` : ""}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button type="button" onClick={() => handleOpenChange(false)}>
                Done
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
